/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-07-17
 */

// Add third-party dependencies.
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Create the authSlice.
export const authSlice = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API,
    prepareHeaders: (headers) => {
      // Get the access token from localStorage.
      const token = localStorage.getItem('accessToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  // Add endpoints here.
  endpoints: (builder) => ({
    // Login (create token).
    loginUser: builder.mutation({
      query: (credentials) => ({
        url: 'auth/jwt/create/',
        method: 'POST',
        body: credentials,
      }),
    }),
    // Refresh token.
    refreshToken: builder.mutation({
      query: (refresh) => ({
        url: 'auth/jwt/refresh/',
        method: 'POST',
        body: refresh,
      }),
    }),
    // Verify token.
    verifyToken: builder.mutation({
      query: (token) => ({
        url: 'auth/jwt/verify/',
        method: 'POST',
        body: token,
      }),
    }),
    // Create user.
    createUser: builder.mutation({
      query: (userData) => ({
        url: 'auth/users/',
        method: 'POST',
        body: userData,
      }),
    }),
    // Activate user.
    activateUser: builder.mutation({
      query: ({ uid, token }) => ({
        url: 'auth/users/activation/',
        method: 'POST',
        body: { uid, token },
      }),
    }),
    // Get Me.
    getMe: builder.query({
      query: () => 'auth/users/me/',
    }),
    // Update Me.
    updateMe: builder.mutation({
      query: (userData) => ({
        url: 'auth/users/me/',
        method: 'PUT',
        body: userData,
      }),
    }),
    // Delete Me.
    deleteMe: builder.mutation({
      query: () => ({
        url: 'auth/users/me/',
        method: 'DELETE',
      }),
    }),
    // Resend activation email.
    resendActivationEmail: builder.mutation({
      query: (email) => ({
        url: 'auth/users/resend_activation/',
        method: 'POST',
        body: { email },
      }),
    }),
    // Reset password.
    resetPassword: builder.mutation({
      query: (email) => ({
        url: 'auth/users/reset_password/',
        method: 'POST',
        body: email,
      }),
    }),
    // Reset password confirm.
    resetPasswordConfirm: builder.mutation({
      query: ({ uid, token, newPassword }) => ({
        url: 'auth/users/reset_password_confirm/',
        method: 'POST',
        body: { uid, token, new_password: newPassword },
      }),
    }),
    // Reset username.
    resetUsername: builder.mutation({
      query: (email) => ({
        url: 'auth/users/reset_username/',
        method: 'POST',
        body: email,
      }),
    }),
    // Reset username confirm.
    resetUsernameConfirm: builder.mutation({
      query: ({ uid, token, newUsername }) => ({
        url: 'auth/users/reset_username_confirm/',
        method: 'POST',
        body: { uid, token, new_username: newUsername },
      }),
    }),
    // Set password.
    setPassword: builder.mutation({
      query: ({ currentPassword, newPassword, reNewPassword }) => ({
        url: 'auth/users/set_password/',
        method: 'POST',
        body: { current_password: currentPassword, new_password: newPassword, re_new_password: reNewPassword },
      }),
    }),
    // Set username.
    setUsername: builder.mutation({
      query: ({ currentPassword, newUsername }) => ({
        url: 'auth/users/set_username/',
        method: 'POST',
        body: { current_password: currentPassword, new_username: newUsername },
      }),
    }),
    // Get users.
    getUsers: builder.query({
      query: () => 'auth/users/',
    }),
    // Get user.
    getUser: builder.query({
      query: (id) => `auth/users/${id}/`,
    }),
    // Update user.
    updateUser: builder.mutation({
      query: ({ id, userData }) => ({
        url: `auth/users/${id}/`,
        method: 'PUT',
        body: userData,
      }),
    }),
    // Delete user.
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `auth/users/${id}/`,
        method: 'DELETE',
      }),
    }),
  }),
});

// Export the auto-generated hooks.
export const {
  useLoginUserMutation,
  useRefreshTokenMutation,
  useVerifyTokenMutation,
  useCreateUserMutation,
  useActivateUserMutation,
  useGetMeQuery,
  useUpdateMeMutation,
  useDeleteMeMutation,
  useResendActivationEmailMutation,
  useResetPasswordMutation,
  useResetPasswordConfirmMutation,
  useResetUsernameMutation,
  useResetUsernameConfirmMutation,
  useSetPasswordMutation,
  useSetUsernameMutation,
  useGetUsersQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = authSlice;
